import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheckered } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const SECTIONS = [
  { id: "revenue", label: "Revenue" },
  { id: "expenses", label: "Expenses" },
  { id: "inventory", label: "Inventory" },
  { id: "reports", label: "Reports" },
];

export default function AccessTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = () =>
    api.get("/accounting/access").then(({ data }) => {
      setUsers(data);
      const d = {};
      data.forEach((u) => { d[u.id] = u.accounting_access || []; });
      setDrafts(d);
    }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const toggle = (userId, section) => {
    setDrafts((prev) => {
      const cur = prev[userId] || [];
      return { ...prev, [userId]: cur.includes(section) ? cur.filter((s) => s !== section) : [...cur, section] };
    });
  };

  const save = async (userId) => {
    setSavingId(userId);
    try {
      await api.put(`/accounting/access/${userId}`, { sections: drafts[userId] || [] });
      toast.success("Access updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="access-tab">
      <div className="flex items-start gap-3 border border-border bg-secondary/40 p-4 text-sm">
        <ShieldCheckered size={20} className="mt-0.5 shrink-0 text-primary" />
        <p className="text-muted-foreground">
          You are the super admin. Grant admins access to specific accounting sections — they will only see the
          sections you enable here. New admins can register from the sign-in page, then appear in this list.
        </p>
      </div>

      <div className="overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-3 font-medium">User</th>
              {SECTIONS.map((s) => <th key={s.id} className="p-3 text-center font-medium">{s.label}</th>)}
              <th className="p-3 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map((u) => (
              <tr key={u.id} className="border-b border-border last:border-0" data-testid={`access-row-${u.id}`}>
                <td className="p-3">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                  {u.is_super_admin && (
                    <span className="mt-1 inline-block rounded-sm border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      Super admin — full access
                    </span>
                  )}
                </td>
                {SECTIONS.map((s) => (
                  <td key={s.id} className="p-3 text-center">
                    {u.is_super_admin ? (
                      <span className="text-xs text-status-confirmed">Always</span>
                    ) : (
                      <Checkbox
                        data-testid={`access-${u.id}-${s.id}`}
                        checked={(drafts[u.id] || []).includes(s.id)}
                        onCheckedChange={() => toggle(u.id, s.id)}
                      />
                    )}
                  </td>
                ))}
                <td className="p-3 text-right">
                  {!u.is_super_admin && (
                    <Button size="sm" data-testid={`access-save-${u.id}`} disabled={savingId === u.id}
                      onClick={() => save(u.id)} className="transition-colors">
                      {savingId === u.id ? "Saving…" : "Save"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users && users.length === 1 && (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="access-empty">
            Only your account exists. New admins can self-register on the sign-in page, then return here to grant them access.
          </div>
        )}
      </div>
    </div>
  );
}
